/**
 * Utility functions for consistent name-to-color mapping.
 * Used for participant avatars and name highlighting throughout the app.
 */

// Name colors - Distinct palette (maximally different hues)
const NAME_COLORS = [
  { text: 'text-rose-500', bg: 'bg-rose-500' },          // Rose
  { text: 'text-emerald-500', bg: 'bg-emerald-500' },    // Emerald
  { text: 'text-blue-500', bg: 'bg-blue-500' },          // Blue
  { text: 'text-amber-500', bg: 'bg-amber-500' },        // Amber
  { text: 'text-violet-500', bg: 'bg-violet-500' },      // Violet
  { text: 'text-teal-500', bg: 'bg-teal-500' },          // Teal
  { text: 'text-orange-500', bg: 'bg-orange-500' },      // Orange
  { text: 'text-indigo-500', bg: 'bg-indigo-500' },      // Indigo
  { text: 'text-fuchsia-500', bg: 'bg-fuchsia-500' },    // Fuchsia
  { text: 'text-red-500', bg: 'bg-red-500' },            // Red
];

// Avatar gradient classes (for avatar circles) - maximally distinct hues
// Ordered to maximize visual difference between adjacent colors
const AVATAR_GRADIENTS = [
  'from-rose-500 to-pink-400',          // 0: Rose/Pink
  'from-emerald-500 to-green-400',      // 1: Emerald/Green
  'from-blue-500 to-sky-400',           // 2: Blue/Sky
  'from-amber-500 to-yellow-400',       // 3: Amber/Yellow
  'from-violet-500 to-purple-400',      // 4: Violet/Purple
  'from-teal-500 to-cyan-400',          // 5: Teal/Cyan
  'from-orange-500 to-amber-400',       // 6: Orange/Amber
  'from-indigo-500 to-blue-400',        // 7: Indigo/Blue
  'from-fuchsia-500 to-pink-400',       // 8: Fuchsia/Pink
  'from-red-500 to-rose-400',           // 9: Red/Rose
];

// Global registry to track participant color assignments
// This ensures consistent colors across components and avoids hash collisions
const participantColorRegistry = new Map();

/**
 * Register participants and assign colors by order (not hash).
 * Call this once when participants list is loaded.
 * @param {string[]} participants - Array of participant names
 */
export function registerParticipants(participants) {
  participantColorRegistry.clear();
  participants.forEach((name, index) => {
    participantColorRegistry.set(name, index % NAME_COLORS.length);
  });
}

/**
 * Get the color index for a name (0-9).
 * Uses registered index if available, falls back to hash.
 * @param {string} name - Participant name
 * @returns {number} Color index
 */
export function getColorIndex(name) {
  // Check registry first (assigned by order)
  if (participantColorRegistry.has(name)) {
    return participantColorRegistry.get(name);
  }
  // Fallback: hash for unregistered names
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % NAME_COLORS.length;
}

/**
 * Get avatar gradient class for a name.
 * @param {string} name - Participant name
 * @returns {string} Tailwind gradient classes (e.g., 'from-amber-600 to-amber-400')
 */
export function getAvatarGradient(name) {
  return AVATAR_GRADIENTS[getColorIndex(name)];
}

/**
 * Get text color class for a name.
 * @param {string} name - Participant name
 * @returns {string} Tailwind text color class (e.g., 'text-amber-600')
 */
export function getNameTextColor(name) {
  return NAME_COLORS[getColorIndex(name)].text;
}

/**
 * Get background color class for a name.
 * @param {string} name - Participant name
 * @returns {string} Tailwind background color class
 */
export function getNameBgColor(name) {
  return NAME_COLORS[getColorIndex(name)].bg;
}

/**
 * Build a map of participant names to their color classes.
 * @param {string[]} participants - Array of participant names
 * @returns {Object} Map of name -> { text, bg, gradient }
 */
export function buildParticipantColorMap(participants) {
  const map = {};
  for (const name of participants) {
    const idx = getColorIndex(name);
    map[name] = {
      text: NAME_COLORS[idx].text,
      bg: NAME_COLORS[idx].bg,
      gradient: AVATAR_GRADIENTS[idx],
    };
  }
  return map;
}

/**
 * Get initials from a name.
 * @param {string} name - Full name
 * @returns {string} Initials (2 characters)
 */
export function getInitials(name) {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

